/**
 * Small building blocks of the app's visual language (see styles/ui.css): pill
 * buttons, the app bar, step progress, fields and notices. Behaviour stays native
 * (buttons, links, inputs) so keyboard, focus and screen readers work as expected.
 */
import { forwardRef, useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import { WarningCircleIcon, InfoIcon } from '@phosphor-icons/react';
import { mediaUrl } from '@/lib/media';

type Variant = 'primary' | 'glass' | 'ghost' | 'danger';

interface ButtonOwnProps {
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
  icon?: PhosphorIcon;
  endIcon?: PhosphorIcon;
  loading?: boolean;
}

function classes(variant: Variant, size: 'sm' | 'md' | 'lg', block: boolean, extra?: string) {
  return ['ui-btn', `ui-btn--${variant}`, size !== 'md' ? `ui-btn--${size}` : '', block ? 'ui-btn--block' : '', extra ?? '']
    .filter(Boolean)
    .join(' ');
}

function ButtonInner({ icon: StartIcon, endIcon: EndIcon, loading, children }: ButtonOwnProps & { children: ReactNode }) {
  return (
    <>
      {loading ? (
        <span className="ui-btn__spinner" aria-hidden="true" />
      ) : StartIcon ? (
        <StartIcon size={18} weight="bold" className="ui-btn__icon" aria-hidden />
      ) : null}
      <span>{children}</span>
      {EndIcon && !loading ? <EndIcon size={18} weight="bold" className="ui-btn__end" aria-hidden /> : null}
    </>
  );
}

export const PillButton = forwardRef<HTMLButtonElement, ButtonOwnProps & ButtonHTMLAttributes<HTMLButtonElement>>(
  function PillButton(
    { variant = 'glass', size = 'md', block = false, icon, endIcon, loading, className, children, disabled, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        className={classes(variant, size, block, className)}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        <ButtonInner icon={icon} endIcon={endIcon} loading={loading}>
          {children}
        </ButtonInner>
      </button>
    );
  },
);

/** Same look as PillButton for navigation (internal routes or tel:/https: links). */
export function PillLink({
  to,
  href,
  variant = 'glass',
  size = 'md',
  block = false,
  icon,
  endIcon,
  children,
  external,
}: ButtonOwnProps & { to?: string; href?: string; children: ReactNode; external?: boolean }) {
  const className = classes(variant, size, block);
  const inner = (
    <ButtonInner icon={icon} endIcon={endIcon}>
      {children}
    </ButtonInner>
  );
  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={href} className={className} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      {inner}
    </a>
  );
}

export function IconButton({
  icon: IconComponent,
  label,
  glass,
  className,
  ...rest
}: { icon: PhosphorIcon; label: string; glass?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={['ui-icon-btn', glass ? 'ui-icon-btn--glass' : '', className ?? ''].filter(Boolean).join(' ')}
      aria-label={label}
      title={label}
      {...rest}
    >
      <IconComponent size={20} aria-hidden />
    </button>
  );
}

/** Top bar: studio logo and short name, an optional status tag and a trailing action. */
export function AppBar({
  logoPath,
  name,
  tag,
  end,
  homeTo,
  className,
}: {
  logoPath: string | null;
  name: string;
  tag?: ReactNode;
  end?: ReactNode;
  homeTo?: string;
  className?: string;
}) {
  const logo = mediaUrl(logoPath);
  const brand = (
    <span className="ui-row" style={{ ['--ui-gap' as string]: '10px' }}>
      {logo ? <img className="ui-appbar__logo" src={logo} alt="" crossOrigin="anonymous" /> : null}
      <span className="ui-appbar__name">{name}</span>
    </span>
  );
  return (
    <header className={className ? `ui-appbar ${className}` : 'ui-appbar'}>
      <span className="ui-grow ui-row" style={{ ['--ui-gap' as string]: '10px' }}>
        {homeTo ? (
          <Link
            to={homeTo}
            className="ui-row"
            style={{ color: 'inherit', textDecoration: 'none' }}
            aria-label={`${name} — на главную`}
          >
            {brand}
          </Link>
        ) : (
          brand
        )}
        {tag}
      </span>
      {end}
    </header>
  );
}

export function StepProgress({ step, total }: { step: number; total: number }) {
  return (
    <div
      className="ui-progress"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={step}
      aria-label={`Шаг ${step} из ${total}`}
    >
      {Array.from({ length: total }, (_, i) => (
        <span key={i} data-on={i < step} />
      ))}
    </div>
  );
}

export const Field = forwardRef<
  HTMLInputElement,
  { label: string; error?: string; hint?: string; multiline?: false } & InputHTMLAttributes<HTMLInputElement>
>(function Field({ label, error, hint, id, className, ...rest }, ref) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedBy = [error ? `${inputId}-error` : '', hint ? `${inputId}-hint` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <label className="ui-field" htmlFor={inputId}>
      <span className="ui-field__label">{label}</span>
      <input
        ref={ref}
        id={inputId}
        className={['ui-input', className ?? ''].filter(Boolean).join(' ')}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      {hint && !error ? (
        <span className="ui-caption" id={`${inputId}-hint`}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span className="ui-field__error" id={`${inputId}-error`} role="alert">
          {error}
        </span>
      ) : null}
    </label>
  );
});

export function Notice({
  tone = 'warn',
  title,
  children,
}: {
  tone?: 'warn' | 'info' | 'error';
  title?: string;
  children?: ReactNode;
}) {
  const Icon = tone === 'info' ? InfoIcon : WarningCircleIcon;
  return (
    <div className={`ui-notice ${tone === 'warn' ? '' : `ui-notice--${tone}`}`} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon size={18} aria-hidden style={{ flex: 'none', marginTop: 1 }} />
      <span className="ui-stack" style={{ ['--ui-gap' as string]: '2px' }}>
        {title ? <strong style={{ fontWeight: 650 }}>{title}</strong> : null}
        {children ? <span>{children}</span> : null}
      </span>
    </div>
  );
}

export function Tag({ tone = 'warn', children }: { tone?: 'warn' | 'accent' | 'muted' | 'ok' | 'bad'; children: ReactNode }) {
  return <span className={`ui-tag ${tone === 'warn' ? '' : `ui-tag--${tone}`}`}>{children}</span>;
}

export function Skeleton({ height, label }: { height: number; label?: string }) {
  return (
    <div className="ui-skeleton" style={{ height }} role={label ? 'status' : undefined} aria-label={label}>
      {label ? <span className="visually-hidden">{label}</span> : null}
    </div>
  );
}
